// src/features/services/media-v2/components/AudioLibrary.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Audio library listing — displays downloaded/ingested audio entities.
// PAC Glass design system.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { useAudioLibrary } from '../hooks/useMediaV2';

export default function AudioLibrary() {
  const { data, isLoading, error } = useAudioLibrary();

  if (isLoading) {
    return (
      <div className="pac-glass rounded-lg p-4 text-sm text-neutral-500 animate-pulse">
        Loading audio library…
      </div>
    );
  }

  if (error) {
    return (
      <div className="pac-glass rounded-lg p-4 text-sm text-red-400">
        Error: {error.message}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="pac-glass rounded-lg p-4 text-sm text-neutral-500">
        No audio entries yet. Use the YouTube tab to ingest your first track.
      </div>
    );
  }

  return (
    <div className="pac-glass rounded-lg divide-y divide-neutral-800">
      {items.map((entry) => (
        <div key={entry.scope_key} className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-neutral-200 truncate">
              {entry.title || entry.filename || entry.scope_key}
            </p>
            <p className="text-xs text-neutral-500 truncate">
              {[entry.artist, entry.album].filter(Boolean).join(' — ') || entry.url}
            </p>
          </div>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
            entry.status === 'completed'
              ? 'bg-green-900/40 text-green-400'
              : entry.status === 'failed'
              ? 'bg-red-900/40 text-red-400'
              : 'bg-cyan-900/40 text-cyan-400'
          }`}>
            {entry.status}
          </span>
          <span className="text-[10px] text-neutral-600 uppercase">
            {entry.codec || 'opus'}
          </span>
        </div>
      ))}
    </div>
  );
}
